import React, { useRef, useEffect, useState } from 'react';

const WheelChart = ({ domains, onDomainClick, editable = true }) => {
  const canvasRef = useRef(null);
  const [hoveredDomain, setHoveredDomain] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, text: '', score: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 60;
    const segments = domains.length;
    const anglePerSegment = (2 * Math.PI) / segments;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background circles
    for (let i = 0; i <= 10; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius / 10) * i, 0, 2 * Math.PI);
      ctx.strokeStyle = i === 10 ? '#ddd' : '#f0f0f0';
      ctx.lineWidth = i === 10 ? 2 : 1;
      ctx.stroke();
    }

    // Draw radial lines
    domains.forEach((_, index) => {
      const angle = anglePerSegment * index - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Draw filled segments
    domains.forEach((domain, index) => {
      const angle = anglePerSegment * index - Math.PI / 2;
      const nextAngle = angle + anglePerSegment;
      const scoreRadius = (radius / 10) * domain.score;

      // Create gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, scoreRadius);
      gradient.addColorStop(0, domain.color + '40');
      gradient.addColorStop(1, domain.color + (hoveredDomain === index ? 'CC' : '99'));

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, scoreRadius, angle, nextAngle);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = domain.color;
      ctx.lineWidth = hoveredDomain === index ? 3 : 2;
      ctx.stroke();
    });

    // Draw labels
    ctx.font = 'bold 13px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    domains.forEach((domain, index) => {
      const angle = anglePerSegment * index - Math.PI / 2;
      const labelRadius = radius + 35;
      const x = centerX + labelRadius * Math.cos(angle);
      const y = centerY + labelRadius * Math.sin(angle);

      // Use customName if available, otherwise fall back to name
      const displayName = domain.customName || domain.name;
      
      // Split long text
      const words = displayName.split(' ');
      if (words.length > 2) {
        ctx.fillStyle = '#333';
        ctx.fillText(words.slice(0, 2).join(' '), x, y - 8);
        ctx.fillText(words.slice(2).join(' '), x, y + 8);
      } else {
        ctx.fillStyle = '#333';
        ctx.fillText(displayName, x, y);
      }
    });

  }, [domains, hoveredDomain]);

  const handleMouseMove = (e) => {
    if (!editable) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) + Math.PI / 2;
    const normalizedAngle = angle < 0 ? angle + 2 * Math.PI : angle;
    const anglePerSegment = (2 * Math.PI) / domains.length;
    const segmentIndex = Math.floor(normalizedAngle / anglePerSegment);
    const radius = Math.min(centerX, centerY) - 60;

    if (distance < radius && segmentIndex >= 0 && segmentIndex < domains.length) {
      setHoveredDomain(segmentIndex);
      setTooltip({
        show: true,
        x: e.clientX,
        y: e.clientY,
        text: domains[segmentIndex].customName || domains[segmentIndex].name,
        score: domains[segmentIndex].score
      });
    } else {
      setHoveredDomain(null);
      setTooltip({ ...tooltip, show: false });
    }
  };

  const handleClick = (e) => {
    if (!editable || !onDomainClick) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) + Math.PI / 2;
    const normalizedAngle = angle < 0 ? angle + 2 * Math.PI : angle;
    const anglePerSegment = (2 * Math.PI) / domains.length;
    const segmentIndex = Math.floor(normalizedAngle / anglePerSegment);
    const radius = Math.min(centerX, centerY) - 60;

    if (distance < radius && segmentIndex >= 0 && segmentIndex < domains.length) {
      onDomainClick(segmentIndex);
    }
  };

  const handleMouseLeave = () => {
    setHoveredDomain(null);
    setTooltip({ ...tooltip, show: false });
  };

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={600}
        height={600}
        className="mx-auto cursor-pointer"
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onMouseLeave={handleMouseLeave}
      />
      {tooltip.show && (
        <div
          className="fixed z-50 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm shadow-lg pointer-events-none"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 40,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="font-semibold">{tooltip.text}</div>
          <div className="text-xs text-gray-300">Score: {tooltip.score}/10</div>
        </div>
      )}
    </div>
  );
};

export default WheelChart;
